import { Fragment, useContext, useEffect, useRef, useState } from "react";
import ErrorBoundary from "../components/global/ErrorBoundary";
import useTestOrderSingleID from "../services/utils/custom hooks/useTestOrderSingleID";
import { useHistory, useParams } from "react-router-dom";
import SummaryTabs from "../components/atomic/SummaryTabs";
import OrderHeaderTickIcon from "../assets/svg/OrderHeaderTickIcon";
import TwoColumnLayout from "../layouts/TwoColumnLayout";
import DetailRenderer from "../components/global/forms/DetailRenderer";
import patientInfo from "../services/models/formModels/patientInfoSummary";
import orderInfoSummary from "../services/models/formModels/orderInfoSummary";
import CustomText from "../components/atomic/CustomText";
import Button from "../components/organism/Button";
import uploadedDocs from "../services/models/formModels/uploadedDocs.model";
import FileActions from "../services/utils/FileActions";
import { useOktaAuth } from "@okta/okta-react";
import toastMessages from "../services/utils/helpers/toastMessage";
import dataConstants from "../constants/dataConstants";
import useQueryParams from "../services/utils/custom hooks/QueryHelper";
import useCreateOrder from "../services/utils/custom hooks/OnlineOrder/useCreateOrder";
import SummarySectionIndicator from "../components/global/LoadingIndicators/SummarySectionIndicator";
import SidebarLoadingIndicator from "../components/global/LoadingIndicators/SidebarLoadingIndicator";
import AdditionalClinicianSummary from "../components/global/forms/AdditionalClinicianSummary";
import AdminContentLayout from "../layouts/AdminContentLayout";
import DocumentManagement from "../components/atomic/DocumentManagement";
import CloseIcon from "../assets/svg/CloseIcon";
import WarningIcon from "../assets/svg/WarningIcon";
import OnlineOrderActions from "../services/OnlineOrderActions";
import { PopUpContext } from "../services/context/popupContext";
import ReportsPopup from "../components/popups/reportsPopup";
import ESignatureICON from "../assets/svg/EsignatureIcon";
import useFeatureFlags from "../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import useProfile from "../services/utils/custom hooks/useProfile";
import StatusHeader from "../components/OrderSummaryScreen/StatusHeader";
import getOrderTestDisplayLabel from "../utils/helpers/getOrderTestDisplayLabel";


function Banner({
  onClick,
  text,
  type,
  subHeading,
  hideButton = false,
  onClose,
}: {
  onClick: any;
  onClose?: any;
  text: string;
  subHeading?: string;
  type: "cancel" | "exception" | "55";
  hideButton: boolean;
}): JSX.Element | null {
  if (type === "cancel") {
    return (
      <div className="xl:mx-28 mx-[20px] py-3 bg-pink border-requiredRed rounded-md border-[1px] mt-9 px-3">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center">
              <CloseIcon
                className="my-auto cursor-pointer mr-3"
                stroke={"#FD4F82"}
                onClick={onClose}
              />
              <CustomText
                text={text}
                fontWeight="font-normal"
                fontSize="lg:!text-base text-sm text-requiredRed"
              />
            </div>
            {subHeading && (
              <div className="ml-[28px] mt-1">
                <CustomText
                  text={
                    <>
                      <span className="font-semibold">
                        Cancellation Reason:{" "}
                      </span>
                      {subHeading}
                    </>
                  }
                  fontWeight="font-normal"
                  fontSize="lg:!text-base text-sm text-requiredRed"
                />
              </div>
            )}
          </div>
          <Button
            textInformation={{
              text: "Reorder",
              fontWeight: "font-semibold",
              fontSize: "lg:!text-base !text-xs",
            }}
            buttonTheme="transparent"
            className="text-requiredRed hover:text-[#f41e1e]"
            onClick={onClick}
          />
        </div>
      </div>
    );
  } else if (type === "exception") {
    return (
      <div className="xl:mx-28 mx-[20px] py-3 flex bg-pink border-requiredRed rounded-md border-[1px] mt-9 items-center justify-between px-3">
        <div>
          <div className="flex items-center pb-3">
            <WarningIcon className="mr-3" />
            <p className="text-requiredRed font-semibold mr-2">
              Missing Information:{" "}
            </p>
            <p className="text-requiredRed mr-2">{subHeading}</p>
          </div>
          <CustomText
            text={text}
            fontWeight="font-normal"
            fontSize="lg:text-base text-sm text-requiredRed"
          />
        </div>
      </div>
    );
  } else if (type === "55") {
    return (
      <div
        onClick={onClick}
        className="xl:mx-28 mx-[20px] py-3 flex bg-pink border-requiredRed rounded-md border-[1px] mt-9 items-center justify-between px-3"
      >
        <div className="flex items-center">
          <ESignatureICON className="my-auto mr-3" stroke={"#FD4F82"} />
          <CustomText
            text={text}
            fontWeight="font-normal"
            fontSize="lg:text-base text-sm text-requiredRed"
          />
        </div>
      </div>
    );
  } else {
    return null;
  }
}

function PatientAndOrderInfo({
  patientInformation,
  orderInformation,
  values,
}: any) {
  const fileActions = new FileActions();
  const { authState } = useOktaAuth();
  const history = useHistory();

  let { id } = useParams<{ id: any }>();
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);

  const [downloadingOrder, setDownloadingOrder] = useState<boolean>(false);
  const [downloadingReport, setDownloadingReport] = useState<boolean>(false);
  const [fetchingReport, setFetchingReports] = useState<boolean>(false);

  async function handleOrderDownload() {
    const primaryTestName = values?.testTypes?.name
      ? values.testTypes.name.split(":")[0]
      : "";
    const secondaryTestName =
      values?.secondaryTestType?.name != null &&
      values.secondaryTestType.name !== ""
        ? values.secondaryTestType.name.split(":")[0]
        : "";
    const testName =
      secondaryTestName !== ""
        ? `${primaryTestName}_${secondaryTestName}`
        : primaryTestName;
    const patientLabel = values?.patientName ? `${values.patientName} ` : "Patient";
    const fileName = `${values?.id}_${patientLabel}_${testName}`;

    setDownloadingOrder(true);
    try {
      const response = await fileActions.downloadMultiplePDF(
        [{ id: values?.id, fileName }],
        authState?.accessToken?.accessToken ?? ""
      );
      setDownloadingOrder(false);
    } catch (error: any) {
      setDownloadingOrder(false);
      toastMessages.error(
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong"
      );
      setDownloadingOrder(false);
    }
    setDownloadingOrder(false);
  }

  async function handleReportDownload() {
    const id = values?.id;
    const fileName = `${values?.patientFirstName} ${values?.patientLastName}`;

    setDownloadingReport(true);
    try {
      const response = await fileActions.downloadReport({
        id,
        token: authState?.accessToken?.accessToken ?? "",
        fileName,
      });

      setDownloadingReport(false);
    } catch (error: any) {
      setDownloadingReport(false);
      toastMessages.error(
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong"
      );
    }
  }

  async function handleViewReports(e: any) {
    e.stopPropagation();
    const id = values?.id;

    setFetchingReports(true);
    try {
      const response = await fileActions.viewReports({
        id,
        token: authState?.accessToken?.accessToken ?? "",
      });

      setFetchingReports(false);

      if (response.length > 0) {
        setVisible(!visible);
        setPopUpTitle("Test Reports");
        setPopUp(<ReportsPopup reports={response} />);
      }
    } catch (error: any) {
      setFetchingReports(false);
      toastMessages.error(
        error?.response?.data?.message ??
        error.message ??
        "Something went wrong"
      );
    }
  }

  return (
    <Fragment>
      <div
        id="patient-order-information"
        className="scroll-my-[260px] xl:flex justify-between mb-5"
      >
        <CustomText
          text="Patient and order information"
          fontWeight="font-medium"
          className="reviewCardHeading"
        />
        <div className="flex flex-wrap xl:mt-0 mt-2">
          <Button
            buttonTheme="secondary"
            textInformation={{
              text: `    ${values?.status < 40 ? "Download Order" : "Download receipt"
                }`,
              fontWeight: "font-semibold",
            }}
            className="mr-3 xs:mb-2"
            disabled={downloadingOrder}
            loading={downloadingOrder}
            onClick={handleOrderDownload}
          />
          <Button
            buttonTheme="secondary"
            textInformation={{
              text: "View report",
              fontWeight: "font-semibold",
            }}
            disabled={
              fetchingReport
                ? true
                : values?.status === 100 &&
                  !!values.orderReport == true &&
                  Array.isArray(values.orderReport) &&
                  values.orderReport.length > 0
                  ? false
                  : true
            }
            className="mr-3 xs:mb-2"
            loading={fetchingReport}
            onClick={handleViewReports}
          />
          <Button
            buttonTheme="secondary"
            textInformation={{
              text: "Download report",
              fontWeight: "font-semibold",
            }}
            disabled={
              downloadingReport
                ? true
                : values?.status === 100 &&
                  !!values.orderReport == true &&
                  Array.isArray(values.orderReport) &&
                  values.orderReport.length > 0
                  ? false
                  : true
            }
            className="mr-3"
            loading={downloadingReport}
            onClick={handleReportDownload}
          />
          <Button
            buttonTheme="primary"
            textInformation={{
              text: "Resume",
              fontWeight: "font-semibold",
            }}
            className=""
            disabled={values?.status > 30}
            onClick={() => history.push(`/access/online-order/${id}`)}
          />
        </div>
      </div>
      {patientInformation && orderInformation && (
        <div className="reviewCard">
          <div>
            <DetailRenderer
              data={patientInformation}
              groupFields={[
                "firstName",
                "middleInitial",
                "lastName",
                "dateOfBirth",
                "email",
              ]}
              hiddenFields={[]}
              obsecureFields={[
                "firstName",
                "middleInitial",
                "lastName",
                "dateOfBirth",
                "email",
              ]}
              MainHeading={"Patient Information"}
            />
          </div>
          <div>
            <DetailRenderer
              data={orderInformation}
              groupFields={[
                "testType",
                "date",
                "organizationName",
                "clinician",
              ]}
              hiddenFields={[]}
              MainHeading={"Order Information"}
            />
          </div>
        </div>
      )}
    </Fragment>
  );
}

export default function OrderSummaryScreen() {
  const history = useHistory();
  const [patientInformation, setPatientInfo] = useState<
    patientInfo | undefined
  >(undefined);
  const [orderInformation, setOrderInfo] = useState<
    orderInfoSummary | undefined
  >(undefined);
  const [uploadedDocs, setUploadedDocs] = useState<uploadedDocs | undefined>(
    undefined
  );
  const [selectedTab, setSelectedTab] = useState<string>(
    "patient-order-information"
  );
  let { id } = useParams<{ id: any }>();
  const { mutate, isPending } = useCreateOrder();
  const {
    isLoading: TestorderLoading,
    data: TestorderData,
    isError: TestorderError,
  } = useTestOrderSingleID(id);

  const patientInfo = TestorderData?.data?.patientInfo;

  const supportingDocs = TestorderData?.data?.supportingDocuments;
  const { data: profile } = useProfile();
  const handleTabClick = (id: any) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setSelectedTab(id);
    }
  };

  const [show, setShow] = useState<boolean>(true);
  const orderActions = new OnlineOrderActions(null, null, null, [], "");
  const { getActionsForSection } = useFeatureFlags();
  const roles = profile?.user?.roles;
  const isDualRoleCOA =
    profile?.user?.roles?.includes("Collaborative Order Agreement") &&
    profile?.user?.roles?.length > 1;
  // const showAttestationBanner = TestorderData?.data?.status === 55 &&
  //   isDualRoleCOA
  const isUserPhysicianHimself =
    Number(TestorderData?.data?.physicianId) === profile?.user?.id;

  const showAttestationBanner =
    TestorderData?.data?.status === 55 &&
    (roles.includes("Office Manager") || isUserPhysicianHimself);

  useEffect(() => {
    if (
      !getActionsForSection(
        dataConstants.FeatureFlagSectionKeys.TEST_ORDERS
      ).find(
        (action) =>
          action.key === dataConstants.SectionActionKeys.VIEW_ORDER_DETAIL
      )?.isActive &&
      !TestorderLoading
    ) {
      history.push("/unauthorized");
      return;
    }

    if (TestorderData?.data) {
      setPatientInfo({
        firstName: patientInfo?.firstName,
        middleInitial: patientInfo?.middleInitial,
        lastName: patientInfo?.lastName,
        dateOfBirth: patientInfo?.birthDate,
        email: patientInfo?.email,
      });
      const testTypeDisplay = getOrderTestDisplayLabel(TestorderData?.data, {
        useFullName: true,
        useFullPrameDescription: true,
      });
      setOrderInfo({
        testType: testTypeDisplay,
        date: TestorderData?.data?.orderDate,
        organizationName: TestorderData?.data?.organization?.name,
        clinician: TestorderData?.data?.physician?.firstName,
      });
      const findDocByType = (type: any) =>
        supportingDocs?.filter((doc: any) => doc?.type === type) || null;

      const filteredUploadedDocs: Partial<uploadedDocs> = {
        patientInfoDemographicSheet: findDocByType("demographics-sheet"),
        insuranceCard: findDocByType("insurance-card"),
        currentPrescriptionMedication: findDocByType(
          "current-prescription-medications"
        ),
        pathologyReport: findDocByType("patholog-report"),
        endoscopyReport: findDocByType("endoscopy-report"),
        clinicNotes: findDocByType("clinic-notes"),
        requisitionForm: findDocByType("requisition-form"),
        visitNotes: findDocByType("visit-notes"),
        lomn: findDocByType("lomn"),
        other: findDocByType("other"),
      };
      const orderSofDocs = findDocByType("order-sof");
      if (orderSofDocs && orderSofDocs.length > 0) {
        filteredUploadedDocs.orderSOF = orderSofDocs;
      }
      const orderSummaryDocs = findDocByType("order-summary");
      if (orderSummaryDocs && orderSummaryDocs.length > 0) {
        filteredUploadedDocs.orderSummary = orderSummaryDocs;
      }

      setUploadedDocs(filteredUploadedDocs as uploadedDocs);
    }
  }, [TestorderData?.data, TestorderLoading]);

  function hideBanner() {
    setShow(!show);
  }

  function handleReOrder() {
    history.push("/access/online-order", {
      orderValue: orderActions.transformResponse(JSON.stringify(TestorderData)),
    });
  }

  const subHeaderTestTitle = getOrderTestDisplayLabel(TestorderData?.data, {
    useFullName: false,
  });

  return (
    <AdminContentLayout
      pageTitle={"Order Summary | CSTL"}
      loading={TestorderLoading}
      subHeaderContainerClassName="overflow-x-hidden"
      subHeaderTitle={`${TestorderData?.data?.patientFirstName} - ${subHeaderTestTitle}`}
      redirect={() => history.goBack()}
    >
      <ErrorBoundary fallbackClassName="col-span-3">
        <TwoColumnLayout className="overflow-auto">
          <div className="bg-sideNavGray border-r">
            {TestorderLoading ? (
              <SidebarLoadingIndicator />
            ) : (
              <SummaryTabs
                handleTabClick={handleTabClick}
                selectedTab={selectedTab}
              />
            )}
          </div>
          <div className="xl:px-[109px] md:mx-5 mx-3  overflow-auto">
            {TestorderLoading ? (
              <SummarySectionIndicator />
            ) : (
              <div className="flex flex-col flex-wrap py-5">
                <div className="w-full ">
                  {((TestorderData?.data?.status === 100 &&
                    TestorderData?.data?.canReOrder) ||
                    (TestorderData?.data?.status === 90 &&
                      TestorderData?.data?.canReOrder)) &&
                    show && (
                      <Banner
                        text={
                          "Test order has failed/Cancelled by Clinical Services"
                        }
                        type="cancel"
                        subHeading={TestorderData?.data?.cancelledReason}
                        onClose={hideBanner}
                        onClick={handleReOrder}
                        hideButton={false}
                      />
                    )}

                  {showAttestationBanner && show && (
                    <Banner
                      text={
                        TestorderData?.data?.eSignatureId === null
                          ? "This order requires COA Attestation. Click here to proceed further"
                          : "This order is already authorized. Castle team will review it and update it soon."
                      }
                      onClose={hideBanner}
                      onClick={() => {
                        if (TestorderData?.data?.eSignatureId === null)
                          history.push("/access/approvals");
                      }}
                      hideButton={false}
                      type="55"
                    />
                  )}
                </div>

                <StatusHeader
                  status={TestorderData?.data?.status ?? 10}
                  selectedTab={selectedTab}
                />

                <PatientAndOrderInfo
                  patientInformation={patientInformation}
                  orderInformation={orderInformation}
                  values={TestorderData?.data}
                />

                <div id="additional-info" className="mt-6">
                  <AdditionalClinicianSummary
                    selectedTab={selectedTab}
                    orderData={TestorderData?.data && TestorderData?.data}
                  />
                </div>
                <DocumentManagement
                  uploadedDocs={uploadedDocs}
                  values={TestorderData?.data}
                />
              </div>
            )}
          </div>
        </TwoColumnLayout>
      </ErrorBoundary>
    </AdminContentLayout>
  );
}
